// ─── components/UI/Badge.jsx ─────────────────────────────────────────────────
// Every count, dot and pill that says "this needs you", in one place.
//
// Before this the same idea was written from scratch in eleven files and had
// drifted into five vocabularies: filled-red at 17/18/20/24px and auto, a 14%
// tint at 18/20px and auto, bare dots at 7/10/12px, muted inline text, and two
// display faces that aren't in the type stack (font-mono in Work.jsx, italic
// serif in WorkQueue.jsx). Nothing capped, so 143 unread messages grew a pill
// until it shoved the rail label out of its row.
//
// The caller never picks a size, a colour or a ring. It picks which of three
// situations it is in, and the form follows. Audited and designed at
// .impeccable/mocks/badge-system/, 2026-09-28.
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// Past nine the exact number stops being actionable — you are opening it
// either way — and the rail has no room to grow. Surfaces with room pass 99.
export const RAIL_CAP = 9;

function capped(value, max) {
  const n = Number(value) || 0;
  return n > max ? `${max}+` : String(n);
}

// A count that changes springs once rather than swapping between paints. It is
// the difference between a number that updated and a number that arrived.
// transform only, so nothing around it reflows; `animate-badge-tick` pairs with
// a prefers-reduced-motion fallback in index.css, like every animation here.
function useTick(value) {
  const [ticking, setTicking] = useState(false);
  const previous = useRef(value);
  useEffect(() => {
    // Only a rise. A count going down is you clearing things, which needs no
    // celebrating, and re-animating on every decrement is just noise.
    if (value > previous.current) {
      setTicking(false);
      // Two frames, so the class is genuinely removed before it is re-added and
      // the animation restarts when the value climbs repeatedly.
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setTicking(true)));
      return () => cancelAnimationFrame(id);
    }
    previous.current = value;
    return undefined;
  }, [value]);
  useEffect(() => { previous.current = value; }, [value, ticking]);
  return ticking ? 'animate-badge-tick' : '';
}

// `label` is the whole accessibility story: pass one and the digits go
// aria-hidden behind it; omit it and the badge is hidden entirely because the
// parent (an icon button, a nav row) already names the thing.
function a11y(label) {
  return label ? {} : { 'aria-hidden': 'true' };
}

// ── Dot ─────────────────────────────────────────────────────────────────────
// Something is unread. Never how much. One diameter, everywhere.
export function BadgeDot({ onArt, ringClass = 'ring-press-card', className }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block h-[7px] w-[7px] flex-none rounded-full bg-notify ${
        // The ring is not decoration: it exists only where the dot sits on
        // artwork (an avatar, an icon) whose colour is unknown. Which colour
        // is the caller's to name — the header bar is `surface`, a card is
        // `press-card` — so it is a ring utility, not a baked-in shadow.
        onArt ? `ring-2 ${ringClass}` : ''
      } ${className || ''}`}
    />
  );
}

BadgeDot.propTypes = {
  onArt: PropTypes.bool,
  ringClass: PropTypes.string,
  className: PropTypes.string,
};

// ── Count ───────────────────────────────────────────────────────────────────
// N things are waiting on you. The only loud form, and the only one that earns
// red — which is what makes red mean something again.
export function BadgeCount({ value, max = 99, label, onArt, ringClass = 'ring-press-card', className }) {
  const tick = useTick(Number(value) || 0);
  if (!Number(value)) return null;
  return (
    <span
      {...a11y(label)}
      className={`inline-grid h-[18px] min-w-[18px] flex-none place-items-center rounded-full bg-notify-deep px-[5px] text-[12px] font-extrabold leading-none tabular-nums text-white ${
        onArt ? `ring-[1.5px] ${ringClass}` : ''
      } ${tick} ${className || ''}`}
    >
      <span aria-hidden={label ? 'true' : undefined}>{capped(value, max)}</span>
      {label && <span className="sr-only">{label}</span>}
    </span>
  );
}

BadgeCount.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  max: PropTypes.number,
  label: PropTypes.string,
  onArt: PropTypes.bool,
  ringClass: PropTypes.string,
  className: PropTypes.string,
};

// ── Tally ───────────────────────────────────────────────────────────────────
// How many are in this bucket: a tab count, a table figure. Same geometry as
// Count so the two sit on one baseline, but grey, because nothing here is
// waiting on anybody. This is what every tab count should have been.
export function BadgeTally({ value, max = 99, label, tone = 'paper', className }) {
  return (
    <span
      {...a11y(label)}
      className={`inline-grid h-[18px] min-w-[18px] flex-none place-items-center rounded-full px-1.5 text-[12px] font-extrabold leading-none tabular-nums ${
        tone === 'ink' ? 'bg-press-paper/[0.16] text-press-paper' : 'bg-press-paper-2 text-press-muted'
      } ${className || ''}`}
    >
      <span aria-hidden={label ? 'true' : undefined}>{capped(value, max)}</span>
      {label && <span className="sr-only">{label}</span>}
    </span>
  );
}

BadgeTally.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  max: PropTypes.number,
  label: PropTypes.string,
  // 'ink' inverts it for the dashboard rail, where grey-on-ink disappears.
  tone: PropTypes.oneOf(['paper', 'ink']),
  className: PropTypes.string,
};
